import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';

import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { deepEqual, ia } from 'lib/helpers/utility';

import { currentPractice } from 'components/practice/practiceState';

import {
  getFinalShapeServiceObj,
  getInitialValues,
  getPayScheduleChanges,
  getServiceValidationSchema
} from '../lib/helpers';

import AllowedSection from './AllowedSection';
import PayerTable from './PayerTable/PayerTable';
import MainSection from './MainSection/MainSection';
import CodingSection from './CodingSection/CodingSection';
import NdcTable from './CodingSection/components/NdcTable';
import DetailsSection from './DetailsSection/DetailsSection';
import PricingSection from './PricingSection/PricingSection';

const ServiceForm = forwardRef(
  (
    {
      service,
      onSave = async () => {},
      isAdminContext = false,
      practice: practiceProp = null,
      onDirtyChange
    },
    ref
  ) => {
    const initialValues = getInitialValues(service);
    const statePractice = useRecoilValue(currentPractice);
    const practice = practiceProp || statePractice;
    const isDental = practice?.kind === 'dental';
    const validationSchema = useMemo(() => getServiceValidationSchema(isDental), [isDental]);

    const formik = useFormik({
      initialValues,
      enableReinitialize: true,
      validationSchema,
      onSubmit: async (values) => {
        const serviceToBeCreated = getFinalShapeServiceObj(values);
        const payScheduleChanges = getPayScheduleChanges(
          values.paySchedule,
          service?.payerConnection
        );
        await onSave(serviceToBeCreated, payScheduleChanges);
      }
    });

    const isFormChanged = !deepEqual(formik.values, initialValues);

    useImperativeHandle(ref, () => ({ submitForm: formik.submitForm }), [formik.submitForm]);

    useEffect(() => {
      onDirtyChange?.(isFormChanged);
    }, [isFormChanged, onDirtyChange]);

    return (
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold text-neutral-900">
            {service?.id ? 'Edit service' : 'Add new service'}
          </span>
          <span className="text-sm text-neutral-600">
            Configure service details, coding, and pricing information.
          </span>
        </div>

        <MainSection formik={formik} service={service} practice={practice} />
        <CodingSection formik={formik} />
        <PricingSection formik={formik} isAdminContext={isAdminContext} />
        {ia(formik.values.ndc_codes) && <NdcTable formik={formik} />}
        <DetailsSection formik={formik} isAdminContext={isAdminContext} practice={practice} />
        {ia(formik.values.paySchedule) && (
          <AllowedSection
            isAdminContext={isAdminContext}
            requiredPermissions="payments.read"
            requiredModules="medipay.payment_suite">
            <PayerTable formik={formik} />
          </AllowedSection>
        )}
      </div>
    );
  }
);

ServiceForm.displayName = 'ServiceForm';

export default ServiceForm;
